import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConfirmationService,
  UserService,
  ValidateService,
} from '../../services';

const CHANGE_IMEI_ERROR_KEYS: { [errorCode: string]: string } = {
  NEW_IMEI_TAKEN: 'CHANGE-IMEI.ERROR-NEW-IMEI-TAKEN',
  NEW_IMEI_NOT_REGISTERED: 'CHANGE-IMEI.ERROR-NEW-IMEI-NOT-REGISTERED',
  OLD_IMEI_NO_PATIENT: 'CHANGE-IMEI.ERROR-OLD-IMEI-NO-PATIENT',
  OLD_IMEI_NOT_REGISTERED: 'CHANGE-IMEI.ERROR-OLD-IMEI-NOT-REGISTERED',
  ACCESS_DENIED: 'CHANGE-IMEI.ERROR-ACCESS-DENIED',
};

@Component({
  selector: 'app-pop-up-change-imei',
  templateUrl: './pop-up-change-imei.component.html',
  styleUrls: ['./pop-up-change-imei.component.scss']
})
export class PopUpChangeImeiComponent implements OnInit, OnDestroy {
  @Input() imei: string = '';

  public changeImeiForm: FormGroup;
  public isSubmitting = false;
  public imeiChecked: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private validateService: ValidateService,
    private userService: UserService,
    private confirmation: ConfirmationService
  ) {
    this.changeImeiForm = this.fb.group({
      oldImei: [
        '',
        [
          Validators.required,
          Validators.pattern('^([0-9]*){15,15}$'),
          this.validateService.imeiValidation,
        ],
      ],
      newImei: [
        '',
        [
          Validators.required,
          Validators.pattern('^([0-9]*){15,15}$'),
          this.validateService.imeiValidation,
        ],
      ],
    });
  }

  ngOnInit(): void {
    if (this.imei) {
      this.changeImeiForm.patchValue({oldImei: this.imei});
      this.checkOldImei();

      this.changeImeiForm
        .get('newImei')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((value: string) => {
          if (value && value.length === 15) {
            this.checkNewImei();
          } else {
            this.imeiChecked = null;
          }
        });

    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Walidacja tylko cyfr
  validateImeiInput(event: Event, controlName: 'oldImei' | 'newImei'): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.changeImeiForm.get(controlName).setValue(input.value);
  }

  // Sprawdź czy stary IMEI istnieje w systemie
  private checkOldImei(): void {
    const oldImei = this.changeImeiForm.get('oldImei').value;

    if (oldImei && oldImei.length === 15) {
      this.userService
        .checkImeiUser(oldImei)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            // IMEI istnieje - OK
          },
          (err) => {
            if (err.data === 'IMEI_NOT_EXISTS') {
              const oldImeiControl = this.changeImeiForm.get('oldImei');
              oldImeiControl.setErrors({ IMEI_NOT_EXISTS: true });
              oldImeiControl.markAsTouched();
            }
          }
        );
    }
  }

  // Sprawdź czy nowy IMEI jest dostępny
  checkNewImei(): void {
    const newImeiControl = this.changeImeiForm.get('newImei');

    if (newImeiControl.valid) {
      const newImei = newImeiControl.value;

      this.userService
        .checkImei(newImei)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.imeiChecked = newImei;
          },
          (err) => {
            const errorData = err.error ? err.error.data : err.data;
            newImeiControl.setErrors({[errorData]: true});
            newImeiControl.markAsTouched();
            this.imeiChecked = null;
          }
        );
    } else {
      this.validateService.validateAllFormFields(this.changeImeiForm);
      this.imeiChecked = null;
    }
  }

  // Licznik znaków dla IMEI
  getImeiLength(controlName: 'oldImei' | 'newImei'): number {
    const value = this.changeImeiForm.get(controlName).value || '';
    return Math.min(value.length, 15);
  }

  isFormValid(): boolean {
    return (
      this.changeImeiForm.valid
    );
  }

  onSubmit(): void {
    if (!this.isFormValid()) {
      this.validateService.validateAllFormFields(this.changeImeiForm);
      return;
    }

    this.isSubmitting = true;

    const credentials = {
      oldImei: this.changeImeiForm.get('oldImei').value,
      newImei: this.changeImeiForm.get('newImei').value,
    };

    this.userService
      .changeImei(credentials)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          // Najpierw zamknij modal pop-up-change-imei
          this.activeModal.close({
            success: true,
            newImei: credentials.newImei
          });

          // Przekierowanie musi zostać w serwisie, a nie w subscribe: `activeModal.close()`
          // wyżej niszczy ten komponent, więc `destroy$` zdążyło już wystrzelić i strumień
          // pod `takeUntil` nie jest miejscem, któremu można powierzyć nawigację.
          this.confirmation.showToastAndNavigate(
            'CHANGE-IMEI.COMUNICATE-AFTER',
            `/users/user/${credentials.newImei}`
          );
        },
        (err) => {
          console.error('Error changing IMEI:', err);
          this.isSubmitting = false;

          const messageKey = CHANGE_IMEI_ERROR_KEYS[err.error_code] || 'CHANGE-IMEI.COMUNICATE-ERROR';
          this.confirmation.showErrorToast(messageKey, credentials);
        }
      );
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

}
